import {
    cloneDeep,
    flowRight,
    get,
    noop,
} from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { findDOMNode } from 'react-dom';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Sticky, StickyContainer } from 'react-sticky';
import styled, { css, withTheme } from 'styled-components';
import { white, grey25 } from '@zocdoc/branding/colors';
import { kievitSlabBook, FontFamily } from '@zocdoc/components/src/typography';
import { breakpoint } from '@zocdoc/components/src/grid';
import { Page, Header as PageHeader, Footer as PageFooter, PaddedContainer } from '@zocdoc/components/src/Layout';
import { Link } from '@zocdoc/components/src/link';
import { SeoBreadcrumbs, SeoRelatedLinks } from '@zocdoc/components/src/Seo';
import { Tabs, Tab } from '@zocdoc/components/src/Tabs';
import { UniversalSearchHeader } from '@zocdoc/components/src/UniversalSearchHeader';
import isMobileOrTabletBreakpoint from '@zocdoc/components/src/utils/isMobileOrTabletBreakpoint';
import { setInsuranceType } from '@zocdoc/components/src/InsurancePicker';
import { InsurancePickerDropdownPrimitives } from '@zocdoc/components/src/InsurancePicker/components/InsurancePickerDropdown';

import { withAB } from '../../config/ab';
import TrackingPixel from '../../config/tracking';
import { TARGETS, EVENT_ACTION } from '../../EventTrackingConstants';
import CarouselMap from '../../Shared/CarouselMap';
import { CrazyEgg } from '../../Shared/CrazyEgg';
import InfoTooltip from '../../Shared/InfoTooltip/view';
import MobileHeaderToggles from '../../Shared/MobileHeaderToggles';
import { withSpotlightEnabled } from '../../Shared/Overlay';
import { TopPopulatedSpecialtyLinks } from '../../Shared/PopulatedSpecialties';
import isADentistrySpecialty from '../../utils/isADentistrySpecialty';
import { Sections } from '../constants';
import {
    NEW_EXISTING_PROFILE_TOGGLE,
    SHOW_REVIEW_COUNT,
    UNIVERSAL_SEARCH_HEADER_PREVIEW,
    WELL_GUIDE_HEADER_NAVIGATION,
} from '../../config/ab/experiments';
import { showWellGuideLink } from '../../config/ab/util';
import { isUniversalSearchHeaderPreviewVariant } from '../experiments/universalSearchHeader';
import { debounceTooltipTracking } from '../utils/debounceTooltipTracking';
import { isEligibleForUniversalSearchHeaderTest } from '../utils/isEligibleForUniversalSearchHeaderTest';
import BookingColumnWithoutSpotlight from './BookingColumn';
import BookingSectionButton from './BookingSectionButton';
import { LOCALIZED_STRINGS } from './constants';
import Header from './Header';
import PictureGallery from './PictureGallery';
import Locations from './ProfileLocations';
import ProfileTab from './ProfileTab';
import Reviews from './Reviews';
import OptOutReviews from './Reviews/OptOutReviews';
import { CrossPageActionTracker } from '../../Shared/CrossPageActionTracking';


const BookingColumnWithSpotlight = withSpotlightEnabled(BookingColumnWithoutSpotlight);
const contentTypeface = kievitSlabBook(16, 24);
const desktopContentTypeface = kievitSlabBook(18, 24);

export class ProfilePage extends PureComponent {
    static propTypes = {
        ab: PropTypes.shape({
            experimentId: PropTypes.string,
            defaultAssignment: PropTypes.string,
            isFeatureFlag: PropTypes.bool,
        }),
        bookingSpotlightIsOn: PropTypes.bool,
        carrierId: PropTypes.string,
        deletePreviewProvider: PropTypes.func,
        deletePreviewProviderFailed: PropTypes.bool,
        deletePreviewProviderSucceeded: PropTypes.bool,
        deletingPreviewProvider: PropTypes.bool,
        initParameters: PropTypes.object,
        planId: PropTypes.string,
        provider: PropTypes.object,
        recordAssignment: PropTypes.func,
        searchQueryGuid: PropTypes.string,
        sessionId: PropTypes.string,
        setupHeaderStoreSubscription: PropTypes.func.isRequired,
        showNewExistingToggle: PropTypes.bool,
        trackClickJoin: PropTypes.func.isRequired,
        trackClickMobileHeaderShowMap: PropTypes.func.isRequired,
        trackClickServiceEmail: PropTypes.func.isRequired,
        trackClickServicePhone: PropTypes.func.isRequired,
        trackClickSignIn: PropTypes.func.isRequired,
        trackClickSignInDropdown: PropTypes.func.isRequired,
        trackClickSignInDropdownLink: PropTypes.func.isRequired,
        trackingId: PropTypes.string,
        trackLegalFooterLinkClicks: PropTypes.func.isRequired,
        trackPageHeaderCTALinkClick: PropTypes.func.isRequired,
        trackPageHeaderLogoClick: PropTypes.func.isRequired,
        trackReviewLinkClick: PropTypes.func.isRequired,
        trackReviewStarsClick: PropTypes.func.isRequired,
        trackSignInLinkClickConversion: PropTypes.func.isRequired,
        trackTabSelect: PropTypes.func.isRequired,
        trackUniversalSearchHeaderSubmit: PropTypes.func,
        trackZocdocFooterLinkClicks: PropTypes.func.isRequired,
        turnBookingSpotlightOff: PropTypes.func.isRequired,
        turnBookingSpotlightOn: PropTypes.func.isRequired,
    };

    static defaultProps = {
        initParameters: {
            doctorLanguageId: '-1',
            procedureId: '-1',
            isNewPatient: true,
        },
        provider: {},
        trackUniversalSearchHeaderSubmit: noop,
    };

    tabs = null;
    setTabIndex = noop;
    isSticky = null;
    previousScrollTop = 0; // keep track of where the user scrolled to on the page previously

    componentDidMount() {
        this.props.setupHeaderStoreSubscription();
        // If eligible for booking eperiment, record assignment
        if (this.props.provider.isBookable && this.props.is1Z) {
            this.props.recordAssignment(NEW_EXISTING_PROFILE_TOGGLE);
        }
    }

    render() {
        const {
            bookingSpotlightIsOn,
            canDeletePreviewProfiles,
            deletePreviewProviderFailed,
            deletePreviewProviderSucceeded,
            deletingPreviewProvider,
            hideMap,
            initParameters,
            is1Z,
            mapIsActive,
            provider,
            selectedLocationId,
            showNewExistingToggle,
            trackDisplayHeaderTitleTooltip,
            trackLegalFooterLinkClicks,
            trackZocdocFooterLinkClicks,
            turnBookingSpotlightOff,
            turnBookingSpotlightOn,
        } = this.props;

        const renderAsideAsModal = !is1Z;

        const currentLocationCloudId = selectedLocationId
            || initParameters.locationCloudId
            || get(provider, 'locations[0].id', '');

        const canShowNewExisting = showNewExistingToggle && provider && provider.isBookable;

        if (deletePreviewProviderSucceeded) {
            return (
                <p>Preview provider deleted successfully.</p>
            );
        }

        return (
            <StyledPage>
                <CrossPageActionTracker />
                {this.renderHeader()}
                <MobileHeaderToggles onClick={this.handleClickToggleMap}>
                    <FormattedMessage
                        defaultMessage="View map"
                        id="Profile.Header.MapToggle"
                    />
                </MobileHeaderToggles>
                <StyledStickyContainer>
                    <ProfilePageWrapper
                        data-test="profilePageWrapper"
                        itemScope
                        itemType={`http://schema.org/${this.getSchemaTypeForProvider(provider)}`}
                    >
                        <Header
                            canDeletePreviewProfiles={canDeletePreviewProfiles}
                            deletePreviewProviderFailed={deletePreviewProviderFailed}
                            deletePreviewProviderSucceeded={deletePreviewProviderSucceeded}
                            deletingPreviewProvider={deletingPreviewProvider}
                            onDeletePreviewProvider={this.handleDeletePreviewProvider}
                            onDisplayTooltip={debounceTooltipTracking(trackDisplayHeaderTitleTooltip)}
                            onReviewLinkClick={this.handleReviewLinkClick}
                            onReviewStarsClick={this.handleReviewStarsClick}
                            provider={provider}
                            shiftPictureUp={canShowNewExisting}
                            showGallery={this.showGallery}
                            showReviewsLink={is1Z}
                        />
                        <StyledAside
                            asModal={renderAsideAsModal}
                            bookingSpotlightIsOn={bookingSpotlightIsOn}
                        >
                            <BookingColumnWithSpotlight
                                currentLocationCloudId={currentLocationCloudId}
                                onClickSpotlightOverlay={turnBookingSpotlightOff}
                                onClickWrappedComponent={turnBookingSpotlightOn}
                                showNewExistingToggle={canShowNewExisting}
                                spotlightIsOn={bookingSpotlightIsOn}
                                {...this.props}
                            />
                        </StyledAside>

                        <ProfileStickyContainer withBorder={renderAsideAsModal}>
                            <Sticky disableCompensation>
                                {stickyProps => this.renderProfileTabs(stickyProps)}
                            </Sticky>
                        </ProfileStickyContainer>

                        {this.maybeRenderSeoLinkFarm()}
                        {this.maybeRenderSeoBreadcrumbs()}

                    </ProfilePageWrapper>

                    <PictureGallery name={provider.approvedFullName} />

                    {mapIsActive ? (
                        <CarouselMap
                            hideMap={hideMap}
                            label={provider.approvedFullName}
                            locationId={currentLocationCloudId}
                            locations={provider.locations}
                            providerId={provider.id}
                        />
                    ) : null}

                    {this.maybeRenderStickyBookAppointmentButton()}
                </StyledStickyContainer>
                <PageFooter
                    onLegalLinkClick={trackLegalFooterLinkClicks}
                    onSeoLinkClick={trackZocdocFooterLinkClicks}
                    onSupportLinkClick={this.handleSupportLinkClick}
                >
                    <TopPopulatedSpecialtyLinks />
                </PageFooter>
                <TrackingPixel />
                <CrazyEgg
                    DesktopSnapshotName="1Z Profile - Desktop"
                    MobileSnapshotName="1Z Profile - MobileWeb"
                    TabletSnapshotName="1Z Profile - Tablet"
                />
            </StyledPage>
        );
    }

    getSchemaTypeForProvider(provider) {
        return isADentistrySpecialty(get(provider, 'relevantSpecialty.id')) ? 'Dentist' : 'Physician';
    }

    maybeRenderStickyBookAppointmentButton() {
        if (!this.props.is1Z) {
            return (
                <BookingAppointmentButtonContainer>
                    <Sticky
                        disableCompensation
                        topOffset={-200}
                    >
                        {
                            ({ style, distanceFromBottom, calculatedHeight }) => {
                                let styleClone = {};
                                const windowInnerHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
                                const realDistanceFromBottom =
                                    (distanceFromBottom + calculatedHeight) - windowInnerHeight;
                                if (realDistanceFromBottom > 0) {
                                    styleClone = cloneDeep(style);
                                    styleClone.background = 'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.75) 20px, rgba(255, 255, 255, 1))';
                                    delete styleClone.top;
                                    styleClone.bottom = 0;
                                }

                                return (
                                    <div style={styleClone}>
                                        <BookingSectionButton provider={this.props.provider} />
                                    </div>
                                );
                            }
                        }
                    </Sticky>
                </BookingAppointmentButtonContainer>
            );
        }
    }

    renderLocations() {
        const {
            expandedLocations,
            provider,
            providerId,
            showMap,
            trackClickIBubbleOfficeLocations,
            theme,
            intl,
        } = this.props;

        const { iBubblesText } = theme.profile;

        const locationTooltipMessage = localizedTooltips(iBubblesText)[Sections.OfficeLocations];
        const tooltipTextIsDefined = locationTooltipMessage.defaultMessage;

        return (
            <ContentContainer>
                <Header3>
                    <FormattedMessage
                        defaultMessage="Office locations"
                        id="Profile.Page.Components.OfficeLocations"
                    />
                    {tooltipTextIsDefined && (
                        <InfoTooltip
                            alt={intl.formatMessage(locationTooltipMessage)}
                            onDisplayTooltip={debounceTooltipTracking(trackClickIBubbleOfficeLocations)}
                        >
                            {intl.formatMessage(locationTooltipMessage)}
                        </InfoTooltip>
                    )}
                </Header3>
                {provider.locations && provider.locations.length ?
                    (
                        <Locations
                            expandedLocations={expandedLocations}
                            expandLocation={this.handleToggleLocationExpanded}
                            locations={provider.locations}
                            onClickExpandMap={showMap}
                            providerId={providerId}
                            showMap={this.handleClickExpandMap}
                        />
                    )
                    : <FormattedMessage {...LOCALIZED_STRINGS.NoOfficeLocations} />
                }
            </ContentContainer>
        );
    }

    renderProfileTabs({ style, distanceFromTop, isSticky }) {
        const { provider } = this.props;
        let stickyStyle;
        this.isSticky = isSticky;

        if (isMobileOrTabletBreakpoint() && distanceFromTop <= 0) {
            stickyStyle = {
                position: 'fixed',
                top: 0,
                background: '#fff',
                width: '100%',
                zIndex: 2,
            };
        }

        const tabHeight = this.tabs ? this.tabs.querySelector('[data-test="tab-link-holder"]').offsetHeight : null;

        return (
            <Tabs
                data-test="profile-tabs"
                innerRef={(node, setTabIndex) => { this.tabs = node; this.setTabIndex = setTabIndex; }}
                isSticky={isSticky}
                onTabClick={this.handleClickTab}
                style={stickyStyle || style}
                tabHeight={tabHeight}
            >
                <Tab
                    data-test="profile-tab"
                    title={<FormattedMessage {...LOCALIZED_STRINGS.ProfileTabLabel} />}
                >
                    <TabContentContainer>
                        <ProfileTab provider={provider} />
                    </TabContentContainer>
                    {this.renderLocations()}
                </Tab>
                {this.maybeRenderReviewsTab()}
            </Tabs>
        );
    }

    renderHeader() {
        const {
            ab,
            carrierId,
            planId,
            provider,
            searchQueryGuid,
            sessionId,
            theme,
            trackingId,
            trackPageHeaderCTALinkClick,
            trackPageHeaderLogoClick,
            trackUniversalSearchHeaderSubmit,
            trackClickSignInDropdown,
            trackClickSignInDropdownLink,
            trackSignInLinkClickConversion,
        } = this.props;

        const universalSearchHeaderProps = {
            carrierId,
            onCTALinkClick: trackPageHeaderCTALinkClick,
            onCustomLinkClick: this.trackClickHeaderCustomLink,
            onLogoClick: trackPageHeaderLogoClick,
            trackSubmitSearch: trackUniversalSearchHeaderSubmit,
            planId,
            searchQueryGuid,
            sessionId,
            setInsuranceType,
            trackingId,
        };

        return isEligibleForUniversalSearchHeaderTest(theme.directoryId, provider.isPreview)
            && isUniversalSearchHeaderPreviewVariant(this.props) ? (
                <StyledUniversalSearchHeader
                    data-prod-guids="zd-preview-profile-universal-search:[32c82089]"
                    {...universalSearchHeaderProps}
                    useSignInDropdown
                />
            ) : (
                <PageHeader
                    onCTALinkClick={trackPageHeaderCTALinkClick}
                    onCustomLinkClick={this.trackClickHeaderCustomLink}
                    onLogoClick={trackPageHeaderLogoClick}
                    onSignInDropdownClick={trackClickSignInDropdown}
                    onSignInDropdownLinkClick={trackClickSignInDropdownLink}
                    showNavigationLinks
                    showWellGuideLink={showWellGuideLink(ab)}
                    trackSignInLinkClickConversion={trackSignInLinkClickConversion}
                    useSignInDropdown
                />
            );
    }

    maybeRenderReviewsTab() {
        const { provider } = this.props;
        const hasReviews = provider.reviews && provider.reviews.length > 0;

        if (hasReviews || provider.optOutOfRatings) {
            const title = <FormattedMessage {...LOCALIZED_STRINGS.ReviewsTabLabel} />;
            return (
                <Tab
                    data-test="review-tab"
                    title={title}
                >
                    {provider.optOutOfRatings
                        ? <OptOutReviews />
                        : <Reviews
                            rating={provider.averageRating}
                            reviewCount={provider.reviewCount}
                            reviews={provider.reviews}
                        />}
                </Tab>
            );
        } else {
            return <div />;
        }
    }

    maybeRenderSeoLinkFarm() {
        if (this.props.seoRelatedLinks && this.props.theme.components.SeoRelatedLinks.enabled) {
            return (
                <SeoRelatedLinksFooter>
                    <SeoRelatedLinks
                        data-test="profile-seo-links"
                        sections={this.props.seoRelatedLinks.sections}
                    />
                </SeoRelatedLinksFooter>
            );
        }
    }

    maybeRenderSeoBreadcrumbs() {
        if (this.props.seo && this.props.seo.breadcrumbs) {
            return (
                <SeoBreadcrumbsFooter>
                    <SeoBreadcrumbs
                        links={this.props.seo.breadcrumbs}
                        onClickSeoBreadcrumb={this.clickSeoBreadcrumb}
                    />
                </SeoBreadcrumbsFooter>
            );
        }
    }

    jumpToReviewTab = event => {
        this.setTabIndex(1);
        const { rating } = event.currentTarget.dataset;
        this.props.trackReviewStarsClick(rating);
    };

    handleReviewStarsClick = event => {
        this.setTabIndex(1);
        const { rating } = event.currentTarget.dataset;
        this.props.trackReviewStarsClick(rating);
    }

    handleReviewLinkClick = () => {
        this.setTabIndex(1);
        this.props.trackReviewLinkClick();
    }

    handleSupportLinkClick = (e, element) => {
        if (element === 'ServicePhone') {
            this.props.trackClickServicePhone();
        } else if (element === 'ServiceEmail') {
            this.props.trackClickServiceEmail();
        }
    };

    clickSeoBreadcrumb = (e, url) => {
        if (url) {
            this.props.trackClickSeoBreadcrumb(url);
        }
    };

    showGallery = event => {
        const target = event.currentTarget;
        const actionName = target.getAttribute('data-action-name') || '';
        let elementName = 'NULL';
        if (actionName === EVENT_ACTION.CLICK_PROVIDER_PICTURE) {
            elementName = TARGETS.PROFILE_PROVIDER_PICTURE_FRAME;
        } else if (actionName === EVENT_ACTION.CLICK_ADDITIONAL_PICTURE_CAPTION_LINK) {
            elementName = TARGETS.PROFILE_ADDITIONAL_PICTURE_CAPTION_LINK;
        }
        this.props.showGallery({
            pictures: this.props.provider.smallPictures,
            action: actionName,
            Element: elementName,
            EventAction: actionName.toLowerCase(),
        });
    };

    trackClickHeaderCustomLink = async e => {
        const { target } = e;
        const url = target.getAttribute('href');
        if (url.indexOf('signin') !== -1) {
            await this.props.trackClickSignIn();
        } else if (url.indexOf('join') !== -1) {
            await this.props.trackClickJoin();
        }
    };

    handleClickToggleMap = () => {
        this.props.showMap();
        this.props.trackClickMobileHeaderShowMap();
    }

    handleClickExpandMap = (ev, loc) => {
        const {
            setLocation,
            showMap,
            trackClickExpandMap,
        } = this.props;
        trackClickExpandMap(loc.id);
        setLocation(loc.id);
        showMap();
    };

    handleToggleLocationExpanded = (ev, loc) => this.props.toggleLocationExpanded(loc.id);

    handleDeletePreviewProvider = () => {
        this.props.deletePreviewProvider(
            this.props.provider.monolithId,
            this.props.provider.personIdentifierSource
        );
    };

    handleClickTab = ev => {
        this.props.trackTabSelect(ev);

        if (this.isSticky) {
            // ensure support for IE as well
            const currentScrollTop = (window.pageYOffset !== undefined) ? window.pageYOffset
                : (document.documentElement || document.body.parentNode || document.body).scrollTop;

            if (this.previousScrollTop) {
                window.scrollTo(0, this.previousScrollTop);
            } else {
                const node = findDOMNode(this.tabs);

                if (node) {
                    node.scrollIntoView();
                }
            }

            this.previousScrollTop = currentScrollTop;
        }
    };
}

const localizedTooltips = iBubblesText => defineMessages({
    [Sections.OfficeLocations]: {
        id: 'Profile.Page.Components.OfficeLocations',
        defaultMessage: iBubblesText[Sections.OfficeLocations],
    },
});

const StyledPage = styled(Page)`
    box-sizing: border-box;
    background-color: ${white};
`;

const ProfilePageWrapper = styled.div`
    color: ${p => p.theme.profile.textColor};

    ${Link} {
        border-bottom: 1px dotted;
        color: ${p => p.theme.profile.linkColor};

        &:hover {
            border-bottom: 1px solid;
            text-decoration: none;
        }
    }
    ${breakpoint.medium} {
        max-width: 62.5%; /* make room for aside */
    }
`;

const BookingAppointmentButtonContainer = styled.div`
    width: 100%;
    position: absolute;
    bottom: 0;
    display: block;
`;

const TabContentContainer = styled(PaddedContainer)`
    ${contentTypeface}
    color: ${p => p.theme.profile.textColor};
    padding-bottom: 20px;

    ${breakpoint.small} {
        padding-top: 16px;
    }

    ${breakpoint.medium} {
        ${desktopContentTypeface}
        padding-top: 14px;
        padding-bottom: 0;
    }
`;

const ContentContainer = styled(TabContentContainer)`
    padding-top: 12px;
`;

const Header1 = styled.h1`
    font: 20px/28px ${FontFamily.SharpSansBold};
    margin-top: 0;

    ${breakpoint.medium} {
        margin-top: 30px;
    }
`;

const Header3 = Header1.withComponent('h3');

const ProfileStickyContainer = styled(StickyContainer)`
    ${p => p.withBorder && css`
        border-top: 1px solid ${grey25};
    `}
    ${breakpoint.medium} {
        border-top: none;
    }
`;

const SeoRelatedLinksFooter = styled.div`
    background-color: ${white};
`;

const SeoBreadcrumbsFooter = styled(ContentContainer)`
    display: flex;
    align-items: center;
    height: 96px;
    background-color: white;
`;

const StyledStickyContainer = styled(StickyContainer)`
    position: relative;
`;

const StyledAside = styled.aside`
    bottom: 0;
    left: 0;
    position: ${p => p.asModal ? 'fixed' : 'static'};
    right: 0;
    top: 0;
    z-index: 10;

    ${p => p.asModal && css`
        ${breakpoint('small', { down: true })} {
            position: fixed;
            display: ${p.bookingSpotlightIsOn ? 'block' : 'none'};
        }
    `}

    ${breakpoint.medium} {
        position: absolute;
        left: 62.5%;
    }
`;

const StyledUniversalSearchHeader = styled(UniversalSearchHeader)`
    ${InsurancePickerDropdownPrimitives.Dropdown} {
        z-index: 12;
    }
`;

export const AB_FLAGS = [
    NEW_EXISTING_PROFILE_TOGGLE,
    SHOW_REVIEW_COUNT,
    UNIVERSAL_SEARCH_HEADER_PREVIEW,
    WELL_GUIDE_HEADER_NAVIGATION,
];

export default flowRight(
    withAB(AB_FLAGS),
    injectIntl,
    withTheme,
)(ProfilePage);
